import React, { useCallback } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { Modal } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import { TypedMemo } from '../../common/types';
import { LoadingView } from '../../common/views/LoadingView';
import { ErrorView } from '../../common/views/ErrorView/ErrorView';
import { useSMLRouter } from '../../../routing/sml/hooks';
import { SMLRoutes } from '../../../routing/sml/SMLRoutes';
import { useSocialPostStats } from '../promote/hooks/useSocialPostStats';
import { SerializedSocialPost } from '../promote/types';
import { SocialPostDailyChart } from './SocialPostDailyChart';
import { SocialPostDetailHead } from './SocialPostDetailHead';
import { SocialPostTiles } from './SocialPostTiles';
import { socialMessages as messages } from './socialMessages';
import { withBrandWord } from '../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet per screen; each part of the screen uses its own slice of it */
import styles from './Social.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SocialPostDetailModalProps {
  accountId: number;
  /** The post that `/social/post/:id` names. Undefined: the detail is closed. */
  postId?: number;
  /** The row that the person clicked, when there is one: the head shows at once from it. */
  post?: SerializedSocialPost;
  /** The period of the daily line in days. */
  days: number;
  onClose: () => void;
}

/**
 * The post detail: a modal on a desktop, a full-height sheet below 769 px (design c1, c4). The
 * route `/social/post/:id` opens it. The head and the tiles show at once from a clicked row; a
 * link opens it with the answer of `GET promo/social-posts/:id/stats`, which also gives the daily
 * line.
 */
const SocialPostDetailModalComponent: React.FC<SocialPostDetailModalProps> = ({
  accountId,
  postId,
  post,
  days,
  onClose,
}) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const stats = useSocialPostStats({ accountId, id: postId, days });
  // The answer has the newest totals of the post. The row shows until it comes.
  const shown = stats.post !== undefined && stats.post.id === postId ? stats.post : post;

  // A story that Music24 published is gone after 24 hours: its note replaces the last-read line.
  const isOurStory = shown?.type === 'story' && shown.scheduledPostUuid !== null;

  const openCalendar = useCallback(() => {
    onClose();
    router.push(SMLRoutes.promoteCalendar);
  }, [onClose, router]);

  return (
    <Modal
      isOpen={postId !== undefined}
      toggle={onClose}
      centered
      className={clsx('sml-modal', styles.detailModal)}
      contentClassName={styles.detailContent}
    >
      {shown === undefined && stats.isLoading ? <LoadingView /> : null}
      {shown === undefined && !stats.isLoading && stats.error !== undefined ? (
        <ErrorView error={stats.error} retry={stats.retry} />
      ) : null}
      {shown === undefined ? null : (
        <div className={styles.detail}>
          <div className={styles.detailBar}>
            <span className={clsx(styles.detailBarTitle, styles.phoneOnly)}>
              {intl.formatMessage(messages.detailTitle)}
            </span>
            <button
              type="button"
              className={styles.detailClose}
              aria-label={intl.formatMessage(messages.detailClose)}
              onClick={onClose}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
          <SocialPostDetailHead post={shown} onOpenCalendar={openCalendar} />
          <SocialPostTiles post={shown} />
          {stats.isLoading ? <LoadingView /> : null}
          {!stats.isLoading && stats.error !== undefined ? <ErrorView error={stats.error} retry={stats.retry} /> : null}
          {!stats.isLoading && stats.error === undefined && stats.days !== undefined ? (
            <SocialPostDailyChart post={shown} days={stats.days} />
          ) : null}
          <div className={styles.detailNote}>
            {isOurStory ? intl.formatMessage(messages.detailStory) : null}
            {!isOurStory && shown.lastReadAt !== null
              ? withBrandWord(
                  intl.formatMessage(messages.detailLastRead, {
                    date: intl.formatDate(new Date(shown.lastReadAt), { month: 'short', day: 'numeric' }),
                    time: intl.formatTime(new Date(shown.lastReadAt), { hour: '2-digit', minute: '2-digit' }),
                  })
                )
              : null}
          </div>
        </div>
      )}
    </Modal>
  );
};

export const SocialPostDetailModal = TypedMemo(SocialPostDetailModalComponent);
